// Base API configuration and utilities - Session-based auth (simplified)
import { API_BASE_URL } from '../../config/api';
import { logger } from '../../utils/logger';
import { checkVersion } from '../../utils/versionCheck';
import { authInitInProgress } from './authState';
import { getPageContext } from './pageContext';
import { trackApiRequestFailed, trackApiRequestCompleted, trackAuthRedirectToLogin } from '../../utils/posthogEvents';
import { getAppBase } from '../../utils/appBase';

// Parse the X-Duration-Ms response header preserving legitimate `0` values.
// `Number(...) || null` would coerce 0 to null and silently drop fast responses
// from p50/p95 analysis — the exact data this header is meant to capture.
// Exported for direct unit testing.
export function parseServerDurationMs(response) {
  const raw = response.headers.get('X-Duration-Ms');
  if (raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

// In-flight requests at this request's arrival. Direct queue-victim signal:
// if a slow endpoint always shows high active counts, queue contention is the
// cause, not the endpoint's own work.
export function parseActiveRequests(response) {
  const raw = response.headers.get('X-Active-Requests');
  if (raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

// Worker's recent loop-lag mean from the last 30s window. May be absent for
// the first ~30s after boot — null is the expected value in that case.
export function parseLoopLagMs(response) {
  const raw = response.headers.get('X-Loop-Lag-Ms');
  if (raw === null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

// Helper function to make requests with session cookie
async function makeRequest(url, options, timeout, externalSignal) {
  const config = {
    ...options,
    credentials: 'include', // Important! Send sid cookie
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  // Use external signal if provided (e.g. from AbortController in component cleanup),
  // otherwise create a timeout-based one
  const controller = externalSignal ? null : new AbortController();
  config.signal = externalSignal || controller.signal;
  const timeoutId = controller ? setTimeout(() => controller.abort(), timeout) : null;

  try {
    const response = await fetch(url, config);
    if (timeoutId) clearTimeout(timeoutId);
    return response;
  } catch (error) {
    if (timeoutId) clearTimeout(timeoutId);
    throw error;
  }
}

// Base fetch wrapper with error handling and timeout
// NEW ARCHITECTURE: Backend auto-refreshes tokens server-side
export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  // Default timeout: 30s for regular requests, 60s for file uploads
  const timeout = options.timeout || (options.body instanceof FormData ? 60000 : 30000);

  // Remove timeout and signal from options before passing to fetch
  const { timeout: _, signal: externalSignal, ...fetchOptions } = options;

  // Track HTTP method on all telemetry so POST /submissions (writes) and
  // GET /submissions?... (reads) don't get merged in latency percentiles.
  const method = (options.method || 'GET').toUpperCase();

  const startTime = Date.now();

  // Snapshot page context at fetch-initiation time, not at telemetry-emit
  // time. If the user navigates during a long in-flight request, emit-time
  // would misattribute the event to the destination page — exactly the
  // pattern this instrumentation was built to detect.
  const { pagePath, pageLoadId } = getPageContext();

  try {
    // Make request - backend handles token refresh automatically
    const response = await makeRequest(url, fetchOptions, timeout, externalSignal);

    // Check if client version matches server version
    const serverVersion = response.headers.get('X-App-Version');
    if (serverVersion) {
      checkVersion(serverVersion);
    }

    // Handle 401 Unauthorized - session expired, redirect to login
    if (response.status === 401) {
      // During auth code exchange, 401 is expected (session not yet established).
      // Don't redirect — AuthContext will complete the exchange and set the cookie.
      if (authInitInProgress) {
        const err = new Error('Auth initialization in progress');
        err.__tracked = true;     // skip PostHog (expected boot state)
        err.__intentional = true; // skip Sentry
        throw err;
      }

      logger.log('❌ Session expired (401) - redirecting to login');

      // Parse the 401 body once to extract the backend reason field
      // (`not_authenticated` / `session_not_found` / `session_expired` /
      // `reauth_required`) so PostHog can split the redirect time series.
      const errorData401 = await response.json().catch(() => ({}));

      // PostHog event BEFORE clearing storage / redirecting — we want the
      // signal even if window.location.href races with PostHog's beacon.
      try {
        trackAuthRedirectToLogin({
          reason: errorData401?.error || 'unknown',
          path: endpoint,
          timeSincePageLoadMs: Math.round(performance.now()),
        });
      } catch { /* never block the redirect on telemetry */ }

      // Clear local state
      localStorage.removeItem('authUser');
      localStorage.removeItem('isAuthenticated');

      // Redirect to login at the app's own root. Derived from the URL, not
      // from import.meta.env.BASE_URL: Phase 4 (VITE_CDN_BASE) made that an
      // absolute CDN URL, and landing on cdn.littlelambenglish.com makes the
      // Authing SDK send a redirect_uri the whitelist rejects.
      window.location.href = `${getAppBase()}/go`; // /go = sign-in flow; bare / is the static landing page
      const err = new Error('Authentication expired - please log in again');
      err.__tracked = true;     // skip PostHog
      err.__intentional = true; // skip Sentry — auth redirect is designed flow
      throw err;
    }

    // (cookie_set_missing detection removed: sid is httpOnly, so document.cookie
    //  can never see it — any check would be a 100% false-positive generator.
    //  Cookie-scoping bugs surface as auth_401_emitted with sid_present:false
    //  on the backend, which is already tracked.)

    // Handle non-OK responses
    if (!response.ok) {
      logger.error(`Response not OK: ${response.status} ${response.statusText} for ${endpoint}`);
      // Non-JSON bodies (e.g. nginx HTML 502 during pm2 restart) return `{}`
      // so the `HTTP {status}: {statusText}` fallback below wins.
      const errorData = await response.json().catch((parseError) => {
        logger.error('Failed to parse error response:', parseError);
        return {};
      });

      // Track for observability (skip 401 — handled by auth flow, not an error)
      if (response.status !== 401 && errorData.error !== 'payment_required') {
        trackApiRequestFailed({
          endpoint,
          method,
          statusCode: response.status,
          durationMs: Date.now() - startTime,
          serverDurationMs: parseServerDurationMs(response),
          errorType: 'http_error',
          pagePath,
          pageLoadId,
        });
      }

      // Special handling for payment_required errors
      if (errorData.error === 'payment_required') {
        // Call paywall handler if registered by PaywallContext
        if (typeof window !== 'undefined' && window.__paywallHandler) {
          const courseCode = errorData.course;
          if (courseCode) {
            window.__paywallHandler(courseCode);
          } else {
            logger.error('payment_required error missing course code:', errorData);
          }
        }

        // Still throw error so calling code knows the request failed
        const err = new Error('payment_required');
        err.__tracked = true;     // skip PostHog
        err.__intentional = true; // skip Sentry — paywall is a designed flow
        throw err;
      }

      // Generic HTTP error — PostHog-tracked above; intentionally NOT flagged
      // __intentional so that reportUserFacingError at the catch site captures
      // it to Sentry. Attach statusCode so callers can classify (e.g. oss.js
      // marks /oss/policy 4xx as intentional file-type validation).
      const err = new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      err.__tracked = true;
      err.statusCode = response.status;
      throw err;
    }

    // Track successful request latency for percentile analysis (p50/p95/p99).
    // serverDurationMs comes from the X-Duration-Ms header set by backend
    // requestLogger middleware — lets us split client-perceived latency
    // (network + queue + serialization) from pure server processing time.
    trackApiRequestCompleted({
      endpoint,
      method,
      statusCode: response.status,
      durationMs: Date.now() - startTime,
      serverDurationMs: parseServerDurationMs(response),
      activeRequests: parseActiveRequests(response),
      loopLagMs: parseLoopLagMs(response),
      pagePath,
      pageLoadId,
    });

    // Handle empty responses
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    }

    return response;
  } catch (error) {
    const durationMs = Date.now() - startTime;

    if (error.name === 'AbortError') {
      // Distinguish component-cleanup aborts (externalSignal) from internal timeouts.
      // External aborts fire on unmount/navigation — not observability errors.
      if (externalSignal?.aborted) {
        throw error;
      }
      trackApiRequestFailed({ endpoint, method, statusCode: null, durationMs, errorType: 'timeout', pagePath, pageLoadId });
      const err = new Error('网络请求超时，请检查网络连接');
      err.__intentional = true; // mobile timeout — PostHog-tracked, not Sentry-worthy
      throw err;
    }

    if (error.name === 'TypeError') {
      trackApiRequestFailed({ endpoint, method, statusCode: null, durationMs, errorType: 'network_error', pagePath, pageLoadId });
      const err = new Error('网络连接失败，请检查网络');
      err.__intentional = true; // ambient mobile connectivity drop — not a bug
      throw err;
    }

    // Track any error not already marked (__tracked is set on http errors above
    // that were already sent to PostHog)
    if (!error.__tracked) {
      trackApiRequestFailed({ endpoint, method, statusCode: null, durationMs, errorType: 'unknown', pagePath, pageLoadId });
    }

    logger.error(`API请求失败 ${endpoint}:`, error.message);
    throw error;
  }
};
